import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Close,
  Save,
  CloudUpload,
  Refresh,
  Image as ImageIcon,
  DeleteSweep,
  RemoveCircleOutline,
  Inventory2,
  Percent,
} from '@mui/icons-material';
import {
  promotionService,
  PromotionBanner,
  PromotionProductItem,
  PromotionProductsResponse,
  AddProductsResult,
} from '../../services/promotionService';
import { API_BASE_URL } from '../../config/constants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SnackState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface BannerFormData {
  title: string;
  subtitle: string;
  cta_text: string;
  cta_url: string;
  sort_order: number;
  is_active: boolean;
  starts_at: string;
  ends_at: string;
  discount_percent: number;
}

interface FamilyOption {
  family_code: string;
  family_name: string;
}

interface SubfamilyOption {
  subfamily_code: string;
  subfamily_name: string;
}

interface CatalogProduct {
  id: string;
  name: string;
  family_code?: string;
  family_name?: string;
  subfamily_code?: string;
  subfamily_name?: string;
}

const EMPTY_FORM: BannerFormData = {
  title: '',
  subtitle: '',
  cta_text: '',
  cta_url: '',
  sort_order: 0,
  is_active: true,
  starts_at: '',
  ends_at: '',
  discount_percent: 0,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getBackofficeHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('backoffice_access_token')}`,
});

/** Convert ISO datetime string to local date input value (YYYY-MM-DD) */
function isoToDateInput(iso: string | null): string {
  if (!iso) return '';
  try {
    return iso.slice(0, 10);
  } catch {
    return '';
  }
}

/** Convert local date input value to ISO string for API */
function dateInputToIso(value: string): string {
  if (!value) return '';
  return `${value}T00:00:00`;
}

// ---------------------------------------------------------------------------
// Tab Panel helper
// ---------------------------------------------------------------------------

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

const PromotionManagementPage: React.FC = () => {
  const { t } = useTranslation();

  // -- List state --
  const [banners, setBanners] = useState<PromotionBanner[]>([]);
  const [loading, setLoading] = useState(true);

  // -- Dialog state --
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<PromotionBanner | null>(null);
  const [form, setForm] = useState<BannerFormData>(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dialogTab, setDialogTab] = useState(0);

  // -- Products tab state --
  const [promoProducts, setPromoProducts] = useState<PromotionProductItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [families, setFamilies] = useState<FamilyOption[]>([]);
  const [subfamilies, setSubfamilies] = useState<SubfamilyOption[]>([]);
  const [allCatalogProducts, setAllCatalogProducts] = useState<CatalogProduct[]>([]);
  const [selectedFamily, setSelectedFamily] = useState('');
  const [selectedSubfamily, setSelectedSubfamily] = useState('');
  const [addingProducts, setAddingProducts] = useState(false);
  const [addResult, setAddResult] = useState<AddProductsResult | null>(null);
  const [removingProductId, setRemovingProductId] = useState<string | null>(null);
  const [removingAll, setRemovingAll] = useState(false);

  // -- Delete state --
  const [deleteTarget, setDeleteTarget] = useState<PromotionBanner | null>(null);
  const [deleting, setDeleting] = useState(false);

  // -- Snackbar --
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnack = (message: string, severity: SnackState['severity'] = 'success') => {
    setSnack({ open: true, message, severity });
  };

  // -- Load banners --
  const loadBanners = useCallback(async () => {
    setLoading(true);
    try {
      const data = await promotionService.listPromotions(1, 100);
      setBanners(data.items);
    } catch (err) {
      console.error('Error loading promotions:', err);
      showSnack(
        err instanceof Error
          ? err.message
          : t('backoffice.promotions.loadError', 'Failed to load promotions'),
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  // -- Load product families/subfamilies from backoffice endpoint --
  const loadCatalogProducts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/backoffice/promotions/product-families`, {
        headers: getBackofficeHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();

      // Set families
      const familyList: FamilyOption[] = (data.families || []).map((f: any) => ({
        family_code: f.family_code,
        family_name: f.family_name,
      }));
      familyList.sort((a, b) => a.family_name.localeCompare(b.family_name));
      setFamilies(familyList);

      // Store all subfamilies for filtering by selected family
      const allProducts: CatalogProduct[] = (data.subfamilies || []).map((sf: any) => ({
        id: '',
        name: '',
        family_code: sf.family_code,
        subfamily_code: sf.subfamily_code,
        subfamily_name: sf.subfamily_name,
      }));
      setAllCatalogProducts(allProducts);
    } catch (err) {
      console.error('Error loading product families:', err);
    }
  }, []);

  // -- Update subfamilies when family changes --
  useEffect(() => {
    if (!selectedFamily) {
      setSubfamilies([]);
      setSelectedSubfamily('');
      return;
    }
    const subfamilyMap = new Map<string, string>();
    allCatalogProducts.forEach((p) => {
      if (p.family_code === selectedFamily && p.subfamily_code && p.subfamily_name) {
        subfamilyMap.set(p.subfamily_code, p.subfamily_name);
      }
    });
    const subfamilyList: SubfamilyOption[] = [];
    subfamilyMap.forEach((name, code) => {
      subfamilyList.push({ subfamily_code: code, subfamily_name: name });
    });
    subfamilyList.sort((a, b) => a.subfamily_name.localeCompare(b.subfamily_name));
    setSubfamilies(subfamilyList);
    setSelectedSubfamily('');
  }, [selectedFamily, allCatalogProducts]);

  // -- Load promotion products --
  const loadPromotionProducts = useCallback(async (promoId: string) => {
    setProductsLoading(true);
    try {
      const data: PromotionProductsResponse = await promotionService.getPromotionProducts(promoId);
      setPromoProducts(data.products);
    } catch (err) {
      console.error('Error loading promotion products:', err);
      showSnack(
        t('backoffice.promotions.productsLoadError', 'Failed to load promotion products'),
        'error',
      );
    } finally {
      setProductsLoading(false);
    }
  }, [t]);

  // -- Form helpers --
  const updateField = <K extends keyof BannerFormData>(key: K, value: BannerFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileSelect = (file: File | null) => {
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  };

  // -- Open create dialog --
  const openCreateDialog = () => {
    setEditingBanner(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setDialogTab(0);
    setPromoProducts([]);
    setAddResult(null);
    setSelectedFamily('');
    setSelectedSubfamily('');
    setDialogOpen(true);
  };

  // -- Open edit dialog --
  const openEditDialog = (banner: PromotionBanner) => {
    setEditingBanner(banner);
    setForm({
      title: banner.title,
      subtitle: banner.subtitle || '',
      cta_text: banner.cta_text || '',
      cta_url: banner.cta_url || '',
      sort_order: banner.sort_order,
      is_active: banner.is_active,
      starts_at: isoToDateInput(banner.starts_at),
      ends_at: isoToDateInput(banner.ends_at),
      discount_percent: banner.discount_percent || 0,
    });
    setImageFile(null);
    setImagePreview(null);
    setDialogTab(0);
    setAddResult(null);
    setSelectedFamily('');
    setSelectedSubfamily('');
    setDialogOpen(true);
    loadPromotionProducts(banner.id);
    loadCatalogProducts();
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingBanner(null);
    setImageFile(null);
    setImagePreview(null);
    setDialogTab(0);
    setPromoProducts([]);
    setAddResult(null);
    setSelectedFamily('');
    setSelectedSubfamily('');
  };

  // -- Save (create or update) --
  const handleSave = async () => {
    if (!form.title.trim()) {
      showSnack(t('backoffice.promotions.titleRequired', 'Title is required'), 'error');
      return;
    }

    if (!editingBanner && !imageFile) {
      showSnack(
        t('backoffice.promotions.imageRequired', 'Please upload a banner image'),
        'error',
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || undefined,
        cta_text: form.cta_text.trim() || undefined,
        cta_url: form.cta_url.trim() || undefined,
        sort_order: form.sort_order,
        is_active: form.is_active,
        starts_at: dateInputToIso(form.starts_at) || undefined,
        ends_at: dateInputToIso(form.ends_at) || undefined,
        discount_percent: form.discount_percent,
      };

      if (editingBanner) {
        await promotionService.updatePromotion(editingBanner.id, payload, imageFile || undefined);
        showSnack(t('backoffice.promotions.updated', 'Promotion updated successfully'));
      } else {
        await promotionService.createPromotion(payload, imageFile!);
        showSnack(t('backoffice.promotions.created', 'Promotion created successfully'));
      }

      closeDialog();
      loadBanners();
    } catch (err) {
      console.error('Error saving promotion:', err);
      showSnack(
        err instanceof Error
          ? err.message
          : t('backoffice.promotions.saveError', 'Failed to save promotion'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  // -- Toggle active status inline --
  const handleToggleActive = async (banner: PromotionBanner) => {
    try {
      await promotionService.updatePromotion(banner.id, {
        is_active: !banner.is_active,
      });
      setBanners((prev) =>
        prev.map((b) =>
          b.id === banner.id ? { ...b, is_active: !b.is_active } : b,
        ),
      );
    } catch (err) {
      console.error('Error toggling active:', err);
      showSnack(
        t('backoffice.promotions.toggleError', 'Failed to update status'),
        'error',
      );
    }
  };

  // -- Delete --
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await promotionService.deletePromotion(deleteTarget.id);
      showSnack(t('backoffice.promotions.deleted', 'Promotion deleted'));
      setDeleteTarget(null);
      loadBanners();
    } catch (err) {
      console.error('Error deleting promotion:', err);
      showSnack(
        err instanceof Error
          ? err.message
          : t('backoffice.promotions.deleteError', 'Failed to delete promotion'),
        'error',
      );
    } finally {
      setDeleting(false);
    }
  };

  // -- Add products to promotion --
  const handleAddProducts = async () => {
    if (!editingBanner) return;
    if (!selectedFamily) {
      showSnack(t('backoffice.promotions.selectFamilyFirst', 'Please select a family first'), 'warning');
      return;
    }

    setAddingProducts(true);
    setAddResult(null);
    try {
      const result = await promotionService.addProductsToPromotion(editingBanner.id, {
        family_code: selectedFamily,
        subfamily_code: selectedSubfamily || undefined,
        discount_percent: form.discount_percent || undefined,
      });
      setAddResult(result);
      showSnack(
        t('backoffice.promotions.productsAdded', 'Added {{added}}, Skipped {{skipped}}', {
          added: result.added,
          skipped: result.skipped,
        }),
        result.conflicts.length > 0 ? 'warning' : 'success',
      );
      loadPromotionProducts(editingBanner.id);
      loadBanners();
    } catch (err) {
      console.error('Error adding products:', err);
      showSnack(
        err instanceof Error
          ? err.message
          : t('backoffice.promotions.addProductsError', 'Failed to add products'),
        'error',
      );
    } finally {
      setAddingProducts(false);
    }
  };

  // -- Remove single product --
  const handleRemoveProduct = async (productId: string) => {
    if (!editingBanner) return;
    setRemovingProductId(productId);
    try {
      await promotionService.removeProductFromPromotion(editingBanner.id, productId);
      setPromoProducts((prev) => prev.filter((p) => p.id !== productId));
      showSnack(t('backoffice.promotions.productRemoved', 'Product removed'));
      loadBanners();
    } catch (err) {
      console.error('Error removing product:', err);
      showSnack(
        t('backoffice.promotions.removeProductError', 'Failed to remove product'),
        'error',
      );
    } finally {
      setRemovingProductId(null);
    }
  };

  // -- Remove all products --
  const handleRemoveAllProducts = async () => {
    if (!editingBanner) return;
    setRemovingAll(true);
    try {
      await promotionService.removeAllProductsFromPromotion(editingBanner.id);
      setPromoProducts([]);
      showSnack(t('backoffice.promotions.allProductsRemoved', 'All products removed'));
      loadBanners();
    } catch (err) {
      console.error('Error removing all products:', err);
      showSnack(
        t('backoffice.promotions.removeAllError', 'Failed to remove all products'),
        'error',
      );
    } finally {
      setRemovingAll(false);
    }
  };

  // -- Render --
  return (
    <Box sx={{ p: 3 }}>
      {/* Header / Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4">
          {t('backoffice.promotions.title', 'Promotion Banners')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadBanners}
            disabled={loading}
          >
            {t('backoffice.promotions.refresh', 'Refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateDialog}
          >
            {t('backoffice.promotions.add', 'Add Promotion')}
          </Button>
        </Box>
      </Box>

      {/* Banner Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : banners.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ImageIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('backoffice.promotions.empty', 'No promotions yet')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'backoffice.promotions.emptyHint',
              'Click "Add Promotion" to create your first banner.',
            )}
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            {t('backoffice.promotions.add', 'Add Promotion')}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {banners.map((banner) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={banner.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: banner.is_active ? 1 : 0.7,
                }}
              >
                <CardMedia
                  component="img"
                  height="180"
                  image={banner.image_url}
                  alt={banner.title}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ flex: 1 }}>
                      {banner.title}
                    </Typography>
                    <Chip
                      label={`#${banner.sort_order}`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1, flexShrink: 0 }}
                    />
                  </Box>
                  {banner.subtitle && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {banner.subtitle}
                    </Typography>
                  )}
                  {/* Discount % and Product Count */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {banner.discount_percent > 0 && (
                      <Chip
                        icon={<Percent sx={{ fontSize: 14 }} />}
                        label={`${banner.discount_percent}% off`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                    {banner.product_count > 0 && (
                      <Chip
                        icon={<Inventory2 sx={{ fontSize: 14 }} />}
                        label={`${banner.product_count} product${banner.product_count !== 1 ? 's' : ''}`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  {banner.cta_text && (
                    <Chip
                      label={banner.cta_text}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  )}
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={banner.is_active}
                          onChange={() => handleToggleActive(banner)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {banner.is_active
                            ? t('backoffice.promotions.active', 'Active')
                            : t('backoffice.promotions.inactive', 'Inactive')}
                        </Typography>
                      }
                    />
                  </Box>
                  {(banner.starts_at || banner.ends_at) && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {banner.starts_at ? isoToDateInput(banner.starts_at) : '...'} -{' '}
                      {banner.ends_at ? isoToDateInput(banner.ends_at) : '...'}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  <Tooltip title={t('backoffice.promotions.edit', 'Edit')}>
                    <IconButton size="small" onClick={() => openEditDialog(banner)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('backoffice.promotions.delete', 'Delete')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(banner)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ================================================================== */}
      {/* Create / Edit Dialog                                               */}
      {/* ================================================================== */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">
            {editingBanner
              ? t('backoffice.promotions.editTitle', 'Edit Promotion')
              : t('backoffice.promotions.createTitle', 'Create Promotion')}
          </Typography>
          <IconButton onClick={closeDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        {/* Tabs: Details | Products (edit only) */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            value={dialogTab}
            onChange={(_e, newVal) => setDialogTab(newVal)}
          >
            <Tab label={t('backoffice.promotions.tabDetails', 'Details')} />
            <Tab
              label={t('backoffice.promotions.tabProducts', 'Products')}
              disabled={!editingBanner}
            />
          </Tabs>
        </Box>

        <DialogContent dividers sx={{ minHeight: 400 }}>
          {/* ---- Details Tab ---- */}
          <TabPanel value={dialogTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Image upload */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('backoffice.promotions.bannerImage', 'Banner Image')}
                  {!editingBanner && ' *'}
                </Typography>
                <Box
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: '2px dashed',
                    borderColor: dragOver ? 'primary.main' : 'grey.400',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: dragOver ? 'action.hover' : 'background.default',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {imagePreview ? (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Preview"
                      sx={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                  ) : editingBanner ? (
                    <Box
                      component="img"
                      src={editingBanner.image_url}
                      alt={editingBanner.title}
                      sx={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 36, color: 'grey.400', mb: 0.5 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {t(
                          'backoffice.promotions.dropzone',
                          'Drag & drop an image here or click to browse',
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Recommended: 1440 x 400px (3.6:1 landscape) &bull; Max 10MB &bull; JPG, PNG, WebP, GIF
                      </Typography>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileSelect(f);
                      e.target.value = '';
                    }}
                  />
                </Box>
                {(imagePreview || editingBanner) && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('backoffice.promotions.clickToReplace', 'Click the image to replace it')}
                  </Typography>
                )}
              </Box>

              {/* Title */}
              <TextField
                label={t('backoffice.promotions.fieldTitle', 'Title')}
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                required
                fullWidth
              />

              {/* Subtitle */}
              <TextField
                label={t('backoffice.promotions.fieldSubtitle', 'Subtitle')}
                value={form.subtitle}
                onChange={(e) => updateField('subtitle', e.target.value)}
                fullWidth
              />

              {/* CTA Text + URL */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('backoffice.promotions.fieldCtaText', 'CTA Text')}
                  value={form.cta_text}
                  onChange={(e) => updateField('cta_text', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={t('backoffice.promotions.fieldCtaUrl', 'CTA URL')}
                  value={form.cta_url}
                  onChange={(e) => updateField('cta_url', e.target.value)}
                  sx={{ flex: 2 }}
                />
              </Box>

              {/* Sort order + Discount % + Active */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label={t('backoffice.promotions.fieldSortOrder', 'Sort Order')}
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => updateField('sort_order', parseInt(e.target.value, 10) || 0)}
                  sx={{ width: 120 }}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label={t('backoffice.promotions.fieldDiscountPercent', 'Discount %')}
                  type="number"
                  value={form.discount_percent}
                  onChange={(e) => {
                    let val = parseFloat(e.target.value) || 0;
                    if (val < 0) val = 0;
                    if (val > 100) val = 100;
                    updateField('discount_percent', val);
                  }}
                  sx={{ width: 140 }}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(e) => updateField('is_active', e.target.checked)}
                    />
                  }
                  label={t('backoffice.promotions.fieldActive', 'Active')}
                />
              </Box>

              {/* Dates */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('backoffice.promotions.fieldStartsAt', 'Starts At')}
                  type="date"
                  value={form.starts_at}
                  onChange={(e) => updateField('starts_at', e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
                <TextField
                  label={t('backoffice.promotions.fieldEndsAt', 'Ends At')}
                  type="date"
                  value={form.ends_at}
                  onChange={(e) => updateField('ends_at', e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
              </Box>
            </Box>
          </TabPanel>

          {/* ---- Products Tab (edit mode only) ---- */}
          <TabPanel value={dialogTab} index={1}>
            {editingBanner ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Add Products Section */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('backoffice.promotions.addProducts', 'Add Products')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                      <InputLabel>
                        {t('backoffice.promotions.family', 'Family')}
                      </InputLabel>
                      <Select
                        value={selectedFamily}
                        label={t('backoffice.promotions.family', 'Family')}
                        onChange={(e) => setSelectedFamily(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>{t('backoffice.promotions.selectFamily', '-- Select Family --')}</em>
                        </MenuItem>
                        {families.map((f) => (
                          <MenuItem key={f.family_code} value={f.family_code}>
                            {f.family_name} ({f.family_code})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }} size="small" disabled={!selectedFamily}>
                      <InputLabel>
                        {t('backoffice.promotions.subfamily', 'Sub-family')}
                      </InputLabel>
                      <Select
                        value={selectedSubfamily}
                        label={t('backoffice.promotions.subfamily', 'Sub-family')}
                        onChange={(e) => setSelectedSubfamily(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>{t('backoffice.promotions.allSubfamilies', '-- All Sub-families --')}</em>
                        </MenuItem>
                        {subfamilies.map((sf) => (
                          <MenuItem key={sf.subfamily_code} value={sf.subfamily_code}>
                            {sf.subfamily_name} ({sf.subfamily_code})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      startIcon={addingProducts ? <CircularProgress size={16} color="inherit" /> : <Add />}
                      onClick={handleAddProducts}
                      disabled={addingProducts || !selectedFamily}
                      size="small"
                    >
                      {t('backoffice.promotions.addProductsBtn', 'Add Products')}
                    </Button>
                  </Box>

                  {/* Add result feedback */}
                  {addResult && (
                    <Box sx={{ mt: 2 }}>
                      <Alert
                        severity={addResult.conflicts.length > 0 ? 'warning' : 'success'}
                        sx={{ mb: addResult.conflicts.length > 0 ? 1 : 0 }}
                      >
                        {t('backoffice.promotions.addResultSummary', 'Added {{added}}, Skipped {{skipped}}', {
                          added: addResult.added,
                          skipped: addResult.skipped,
                        })}
                      </Alert>
                      {addResult.conflicts.length > 0 && (
                        <Alert severity="warning" variant="outlined">
                          <Typography variant="body2" gutterBottom>
                            {t('backoffice.promotions.conflictsTitle', 'Conflicts (products already in another promotion):')}
                          </Typography>
                          {addResult.conflicts.map((c, idx) => (
                            <Typography key={idx} variant="caption" display="block">
                              {c.product_name || c.product_id} — {c.existing_promotion || 'unknown promotion'}
                            </Typography>
                          ))}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Paper>

                {/* Products List */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      {t('backoffice.promotions.productsList', 'Products in this Promotion')}
                      {' '}({promoProducts.length})
                    </Typography>
                    {promoProducts.length > 0 && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={removingAll ? <CircularProgress size={14} color="inherit" /> : <DeleteSweep />}
                        onClick={handleRemoveAllProducts}
                        disabled={removingAll}
                      >
                        {t('backoffice.promotions.removeAll', 'Remove All')}
                      </Button>
                    )}
                  </Box>

                  {productsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : promoProducts.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('backoffice.promotions.noProducts', 'No products assigned to this promotion yet.')}
                      </Typography>
                    </Paper>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('backoffice.promotions.colPartNumber', 'Part Number')}</TableCell>
                            <TableCell>{t('backoffice.promotions.colName', 'Name')}</TableCell>
                            <TableCell>{t('backoffice.promotions.colFamily', 'Family')}</TableCell>
                            <TableCell align="right">{t('backoffice.promotions.colDiscount', 'Discount %')}</TableCell>
                            <TableCell align="center">{t('backoffice.promotions.colActions', 'Actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {promoProducts.map((pp) => (
                            <TableRow key={pp.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {pp.product_part_number || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {pp.product_name || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {pp.product_family || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${pp.effective_discount}%`}
                                  size="small"
                                  color={pp.discount_percent !== null ? 'secondary' : 'default'}
                                  variant={pp.discount_percent !== null ? 'filled' : 'outlined'}
                                  title={
                                    pp.discount_percent !== null
                                      ? t('backoffice.promotions.perProductOverride', 'Per-product override')
                                      : t('backoffice.promotions.promotionDefault', 'Promotion default')
                                  }
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={t('backoffice.promotions.removeProduct', 'Remove')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveProduct(pp.id)}
                                    disabled={removingProductId === pp.id}
                                  >
                                    {removingProductId === pp.id ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <RemoveCircleOutline fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(
                  'backoffice.promotions.productsEditOnly',
                  'Save the promotion first, then edit it to manage products.',
                )}
              </Typography>
            )}
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>
            {t('backoffice.promotions.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          >
            {editingBanner
              ? t('backoffice.promotions.save', 'Save')
              : t('backoffice.promotions.create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================== */}
      {/* Delete Confirmation Dialog                                         */}
      {/* ================================================================== */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>
          {t('backoffice.promotions.deleteConfirmTitle', 'Delete Promotion?')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'backoffice.promotions.deleteConfirmMessage',
              'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
              { title: deleteTarget?.title || '' },
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('backoffice.promotions.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {t('backoffice.promotions.confirmDelete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================== */}
      {/* Snackbar                                                           */}
      {/* ================================================================== */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PromotionManagementPage;
